import React from "react";
import { Autocomplete, TextField, Typography, createFilterOptions } from "@mui/material";
import { Category } from "../Services/Category/CategoryApi";
import { useCategories } from "../Services/Category/CategoryHooks";
import { FieldError } from "react-hook-form";

interface CategoryAutocompleteProps {
    onChange: (selected: Category) => void;
    onBlur: () => void;
    value?: Category;
    error?: FieldError;
}

const filter = createFilterOptions<Category & { inputValue?: string }>();

const CategoryAutocomplete = React.forwardRef<any, CategoryAutocompleteProps>(({ onChange, onBlur, value, error }, ref) => {
    const { data: categories } = useCategories();

    const handleChange = (event: any, newValue: any) => {
        if (typeof newValue === 'string') {
            onChange({
                name: newValue,
            } as Category);
        } else if (newValue && newValue.inputValue) {
            // Create a new value from the user input
            onChange({
                name: newValue.inputValue,
            } as Category);
        } else {
            onChange(newValue);
        }
    };

    const handleFilterOptions = (options: Category[], params: any) => {
        const filtered = filter(options, params);

        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting) {
            filtered.push({
                inputValue,
                name: `Add "${inputValue}"`,
            } as Category & { inputValue: string });
        }

        return filtered;
    };

    const handleGetOptionLabel = (option: any) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
            return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
            return option.inputValue;
        }
        // Regular option
        return option.name;
    };

    return (
        <>
            {categories ? (
                <Autocomplete
                    value={value ?? null}
                    onChange={handleChange}
                    filterOptions={handleFilterOptions}
                    selectOnFocus
                    clearOnBlur
                    onBlur={onBlur}
                    handleHomeEndKeys
                    options={categories}
                    getOptionLabel={handleGetOptionLabel}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                            <li key={key} {...optionProps}>
                                {option.name}
                            </li>
                        );
                    }}
                    sx={{ width: 300 }}
                    freeSolo
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Category"
                            inputRef={ref}
                            error={!!error}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            ) : (
                <Typography>Loading Categories</Typography>
            )}
        </>
    );
});

export default CategoryAutocomplete;